import {
  Component,
  TemplateFunction,
  HttpService,
  HttpServiceResponse,
} from '@ribajs/core'
import { Pjax } from '@ribajs/router'
import { ShopifyPaginate, ShopifyPart } from '../types/index.js'
import { redirectToPath } from '../utils/index.js'
import pRetry from 'p-retry'

interface Scope {
  // OPTIONS
  /** The default paginate object from shopify */
  paginate?: ShopifyPaginate
  /**
   * The pagination mode.
   * - `dynamic` - The content of the next page is dynamically loaded and appended without reloading the page.
   * - `reload` - This is the classic mode, the next page is reloaded like a simple link.
   */
  mode: 'dynamic' | 'reload'
  /**
   * The container selector you want to get the content from and to append the new children to.
   * This can be a string or an array of strings, if this is a
   * - `string` - Then there is only one container
   * - `string[]` - Then there are multiple containers, this is the case, for example, on the search page for containers for the tabs "Products", "Articles" and "Pages".
   */
  containerSelector?: string | string[]
  /** The children selector, used to get the children from the new container and to append them to the old container */
  childrenSelector?: string
  /** If true the loading spinner shown */
  loading: boolean
  /** If true the browser url will be changed after loading the next / prev page */
  updateUrl: boolean
  /** If true the previous pages will be loaded automatically if the user starts on a page > 1 */
  autoloadPreviousPages: boolean
  // METHODS
  /** The complete process to load the next page */
  loadNextPage: SbPaginationComponent['loadNextPage']
  /** The complete process to load the previous page */
  loadPrevPage: SbPaginationComponent['loadPrevPage']
  // TEMPLATES
}

enum Direction {
  APPEND,
  PREPEND,
}

/**
 * A pagination component to load the next content dynamically
 * @note If you are looking for the pagination of a collection please see `src/riba/components/sb-collection.component.ts`
 */
export class SbPaginationComponent extends Component {
  public static tagName = 'sb-pagination'

  public scope: Scope = {
    // OPTIONS
    paginate: undefined,
    mode: 'dynamic',
    containerSelector: undefined,
    childrenSelector: undefined,
    loading: false,
    updateUrl: true,
    autoloadPreviousPages: true,
    // METHODS
    loadNextPage: this.loadNextPage.bind(this),
    loadPrevPage: this.loadPrevPage.bind(this),
    // TEMPLATES
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return [
      'paginate',
      'container-selector',
      'children-selector',
      'update-url',
      'autoload-previous-pages',
      'mode',
    ]
  }

  /**
   * Attributes that must be passed to the custom element, if not all attributes are truthy,
   * the component will not be initialized and an error message will be issued.
   * @returns
   */
  protected requiredAttributes(): string[] {
    return ['paginate']
  }

  /** Called after this custom element is bound and ready */
  async afterBind() {
    await super.afterBind()

    if (this.scope.mode === 'reload') {
      this.scope.autoloadPreviousPages = false
      this.scope.updateUrl = false
    }

    await this.autoloadPreviousPages()
    this.classList.add(`${SbPaginationComponent.tagName}-ready`)
  }

  /** Autoload all previous pages if enabled */
  protected async autoloadPreviousPages() {
    if (!this.scope.paginate) {
      console.warn('[sb-pagination] Paginate object not set!')
      return
    }

    const currentPageNumber = this.scope.paginate.current_page

    if (currentPageNumber > 1 && this.scope.autoloadPreviousPages) {
      for (let i = currentPageNumber - 1; i >= 1; i--) {
        const page = this.scope.paginate.parts[i - 1]
        await this.loadPage(page, Direction.PREPEND)
      }
    }

    this.scope.paginate.previous = null
  }

  protected updateBrowserUrl(page: ShopifyPart) {
    window.history.replaceState(null, '', page.url)
  }

  /** Updates the `paginate` object for the next page */
  protected jumpToNextPage() {
    if (!this.scope.paginate) {
      console.warn('[sb-pagination] Paginate object not set!')
      return
    }

    const currentPageNumber = this.scope.paginate.current_page
    if (currentPageNumber >= this.scope.paginate.pages) {
      console.error('[sb-pagination] You are already on the last page!')
      return
    }

    this.scope.paginate.current_page++

    this.scope.paginate.previous = this.scope.paginate.next
    this.scope.paginate.next =
      this.scope.paginate.parts[this.scope.paginate.current_page] || null
  }

  /** Updates the `paginate` object for the previous page */
  protected jumpToPreviousPage() {
    if (!this.scope.paginate) {
      console.warn('[sb-pagination] Paginate object not set!')
      return
    }

    const currentPageNumber = this.scope.paginate.current_page
    if (currentPageNumber <= 1) {
      console.error('[sb-pagination] You are already on the last page!')
      return
    }

    this.scope.paginate.current_page--

    this.scope.paginate.next = this.scope.paginate.previous
    this.scope.paginate.previous =
      this.scope.paginate.parts[this.scope.paginate.current_page - 1] || null
  }

  protected async goToPagePage(page: ShopifyPart) {
    if (window.router_enable) {
      Pjax.getInstance()?.goTo(page.url)
    } else {
      redirectToPath(page.url)
    }
  }

  /** Loads a page (next, previous or any other) and appends the new child elements to the container */
  protected async loadPage(page: ShopifyPart, dir: Direction) {
    if (!this.scope.containerSelector) {
      console.warn('[sb-pagination] containerSelector not set!')
      return
    }

    const containerSelectors: string[] = Array.isArray(
      this.scope.containerSelector,
    )
      ? this.scope.containerSelector
      : [this.scope.containerSelector]

    const childrenSelector = this.scope.childrenSelector

    if (!containerSelectors.length) {
      console.warn('[sb-pagination] containerSelector not set!')
      return
    }

    if (!childrenSelector) {
      console.warn('[sb-pagination] childrenSelector not set!')
      return
    }

    const res: HttpServiceResponse<any> = await pRetry(
      HttpService.get.bind(HttpService, page.url),
    )

    const parser = new DOMParser()
    const nextPageDoc = parser.parseFromString(res.body, 'text/html')

    for (const containerSelector of containerSelectors) {
      const _newChildren = nextPageDoc
        .querySelector(containerSelector)
        ?.querySelectorAll(childrenSelector)

      if (!_newChildren) {
        console.warn(
          `[sb-pagination] No new children's with selector "${containerSelector} ${childrenSelector}" found!`,
        )
        continue
      }

      const newChildren = Array.from(_newChildren)

      const container = document.querySelector(containerSelector)
      if (!container) {
        console.warn(
          `[sb-pagination] Container with selector ${containerSelector} not found!`,
        )
        continue
      }

      if (dir === Direction.APPEND) {
        container.append(...newChildren)
      } else {
        container.prepend(...newChildren)
      }
    }
  }

  /** The complete process to load the next page */
  public async loadNextPage(event?: Event, updateUrl = this.scope.updateUrl) {
    if (!this.scope.paginate) {
      console.warn('[sb-pagination] Paginate object not set!')
      return
    }

    if (!this.scope.paginate.next) {
      console.warn('[sb-pagination] You are already on the last page!')
      return
    }

    event?.preventDefault()
    event?.stopPropagation()

    this.scope.loading = true

    if (this.scope.mode === 'reload') {
      return this.goToPagePage(this.scope.paginate.next)
    }

    try {
      await this.loadPage(this.scope.paginate.next, Direction.APPEND)
    } catch (error) {
      console.error(error)
    }

    if (updateUrl) this.updateBrowserUrl(this.scope.paginate.next)
    this.jumpToNextPage()
    this.scope.loading = false
  }

  /** The complete process to load the previous page */
  public async loadPrevPage(event?: Event, updateUrl = this.scope.updateUrl) {
    if (!this.scope.paginate) {
      console.warn('[sb-pagination] Paginate object not set!')
      return
    }

    if (!this.scope.paginate.previous) {
      console.warn('[sb-pagination] You are already on the first page!')
      return
    }

    event?.preventDefault()
    event?.stopPropagation()

    this.scope.loading = true

    if (this.scope.mode === 'reload') {
      return this.goToPagePage(this.scope.paginate.previous)
    }

    try {
      await this.loadPage(this.scope.paginate.previous, Direction.PREPEND)
    } catch (error) {
      console.error(error)
    }

    if (updateUrl) this.updateBrowserUrl(this.scope.paginate.previous)
    this.jumpToPreviousPage()
    this.scope.loading = false
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
